import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ variant, detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (!variant) {
          this.#updateFirstMediaForSelectedOptions(newMediaGallery, event.selectedOptions);
          return;
        }

        const currentFirstImage = this.querySelector('.media-gallery__grid .product-media__image');
        const nextFirstImage = newMediaGallery.querySelector('.media-gallery__grid .product-media__image');
        const shouldResetMediaScroll =
          window.matchMedia('(min-width: 990px)').matches &&
          window.location.pathname.startsWith('/products/') &&
          !this.closest('dialog') &&
          currentFirstImage instanceof HTMLImageElement &&
          nextFirstImage instanceof HTMLImageElement &&
          currentFirstImage.getAttribute('src') !== nextFirstImage.getAttribute('src');

        this.replaceWith(newMediaGallery);

        if (shouldResetMediaScroll) {
          requestAnimationFrame(() => {
            const firstMedia = newMediaGallery.querySelector('.media-gallery__grid > .product-media-container');
            if (!(firstMedia instanceof HTMLElement)) return;

            const headerHeight = document.querySelector('#header-component')?.getBoundingClientRect().height ?? 0;
            firstMedia.style.scrollMarginTop = `${headerHeight}px`;
            firstMedia.scrollIntoView({ behavior: 'instant', block: 'start' });
            firstMedia.style.removeProperty('scroll-margin-top');
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #updateFirstMediaForSelectedOptions(newMediaGallery, selectedOptions) {
    const selectedColor = selectedOptions.find(({ name }) => name.toLowerCase() === 'color')?.value;
    if (!selectedColor) return;

    const normalize = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const selectedColorKey = normalize(selectedColor);
    const nextSlide = Array.from(newMediaGallery.querySelectorAll('slideshow-slide')).find((slide) => {
      const image = slide.querySelector('.product-media__image');
      if (!(image instanceof HTMLImageElement)) return false;

      return normalize(`${image.alt} ${image.src}`).includes(selectedColorKey);
    });
    const currentSlide = this.querySelector('slideshow-slide');
    const currentMedia = currentSlide?.querySelector('.product-media');
    const nextMedia = nextSlide?.querySelector('.product-media');
    if (!currentSlide || !currentMedia || !nextMedia) return;

    const currentImage = currentMedia.querySelector('.product-media__image');
    const nextImage = nextMedia.querySelector('.product-media__image');
    const imageChanged =
      currentImage instanceof HTMLImageElement &&
      nextImage instanceof HTMLImageElement &&
      currentImage.getAttribute('src') !== nextImage.getAttribute('src');

    currentMedia.replaceWith(nextMedia.cloneNode(true));

    if (imageChanged && window.matchMedia('(min-width: 990px)').matches && !this.closest('dialog')) {
      requestAnimationFrame(() => {
        const headerHeight = document.querySelector('#header-component')?.getBoundingClientRect().height ?? 0;
        currentSlide.style.scrollMarginTop = `${headerHeight}px`;
        currentSlide.scrollIntoView({ behavior: 'instant', block: 'start' });
        currentSlide.style.removeProperty('scroll-margin-top');
      });
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
